import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TextInput } from 'react-native';
import { useTranslation } from '@bonanza-social/i18n';
import { trackEvent, useDispatch } from '@bonanza-social/toolkit/state';
import { InputProps } from './Input.types';
import { getInputContainerStyle, getInputStyle } from './Input.utils';
import {
  CLEAR_ICON_STYLE,
  DROP_DOWN_ICON_STYLE,
  INPUT_ICON_STYLE,
  SECURE_TEXT_ICON_STYLE,
} from './Input.content';
import Box from '../Box/Box';
import useStyle from '../../hooks/useStyle';
import FloatingPlaceholder from './Placeholder/FloatingPlaceholder';
import Icon from '../Icon/Icon';
import Image from '../Image/Image';
import InputButton from './InputButton/InputButton';

function Input(
  {
    value,
    icon,
    testID='input-container',
    autoFocus,
    maxLength,
    multiline,
    numberOfLines,
    placeholder,
    placeholderKey,
    placeholderNS,
    autoComplete,
    autoCapitalize,
    autoCorrect,
    inputMode,
    keyboardType,
    enterKeyHint,
    secureTextEntry,
    importantForAutofill,
    textContentType,
    placeholderTextColor,
    isPlaceholderFloating = !icon,
    hideClearIcon,
    showDropdownIcon,
    disabled,
    editable = true,
    error,
    style,
    trackingId,
    image,
    onSubmitEditing,
    onChangeText,
    onPressIn,
    onFocus,
    onBlur,
    submitBehavior,
    ...rest
  }: InputProps,
  ref: ForwardedRef<TextInput>,
) {
  const dispatch = useDispatch();
  const { t } = useTranslation(placeholderNS);
  const [focused, setFocused] = useState(false);
  const [hideSecureField, setHideSecureField] = useState(secureTextEntry);
  const containerStyle = useMemo(
    () => getInputContainerStyle(error, focused, disabled),
    [error, focused, disabled],
  );
  const inputStyle = useStyle(
    useMemo(
      () => ({ ...getInputStyle(disabled, isPlaceholderFloating), ...style }),
      [disabled, isPlaceholderFloating, style],
    ),
  );

  const handleFocus = useCallback<Required<InputProps>['onFocus']>(
    (event) => {
      setFocused(true);
      onFocus?.(event);

      dispatch(
        trackEvent({
          name: 'input_focus',
          params: {
            inputLabel: placeholder || `${placeholderNS}.${placeholderKey}`,
            inputTrackingId: trackingId,
          },
        }),
      );
    },
    [dispatch, onFocus, placeholder, placeholderKey, placeholderNS, trackingId],
  );

  const handleBlur = useCallback<Required<InputProps>['onBlur']>(
    (event) => {
      setFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleClear = useCallback(() => {
    onChangeText?.('');
  }, [onChangeText]);

  const handleSecureInput = useCallback(() => {
    setHideSecureField(!hideSecureField);
  }, [hideSecureField, setHideSecureField]);

  useEffect(() => {
    if (error && !focused) {
      dispatch(
        trackEvent({
          name: 'input_error',
          params: {
            inputLabel: placeholder || `${placeholderNS}.${placeholderKey}`,
            inputTrackingId: trackingId,
            inputError: error,
          },
        }),
      );
    }
  }, [
    dispatch,
    error,
    focused,
    placeholder,
    placeholderKey,
    placeholderNS,
    trackingId,
  ]);

  return (
    <Box {...containerStyle} {...rest}>
      {icon && <Icon name={icon} {...INPUT_ICON_STYLE} />}
      {image && (
        <Image
          source={image.source}
          width={image.width || 20}
          marginLeft="space-200"
        ></Image>
      )}
      <Box flex={1}>
        <TextInput
          ref={ref}
          style={inputStyle}
          value={value}
          testID={testID}
          keyboardAppearance="dark"
          autoFocus={autoFocus}
          multiline={multiline}
          numberOfLines={numberOfLines}
          maxLength={maxLength}
          placeholder={
            isPlaceholderFloating
              ? undefined
              : (placeholder ?? (placeholderKey && t(placeholderKey)))
          }
          placeholderTextColor={placeholderTextColor}
          autoComplete={autoComplete}
          editable={editable}
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          keyboardType={keyboardType}
          inputMode={inputMode}
          enterKeyHint={enterKeyHint}
          secureTextEntry={hideSecureField}
          importantForAutofill={importantForAutofill}
          onSubmitEditing={onSubmitEditing}
          textContentType={textContentType}
          onChangeText={onChangeText}
          submitBehavior={submitBehavior}
          onPressIn={onPressIn}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {placeholderKey && isPlaceholderFloating ? (
          <FloatingPlaceholder
            direction={value || focused ? 'up' : 'down'}
            locKey={placeholderKey}
            locNS={placeholderNS}
          />
        ) : null}
      </Box>
      {focused && value && secureTextEntry ? (
        <InputButton {...SECURE_TEXT_ICON_STYLE} onPressIn={handleSecureInput}>
          <Icon
            name={hideSecureField ? 'visibility' : 'visibilityOff'}
            gradient="iconGrayScale"
          />
        </InputButton>
      ) : null}
      {focused && value && !hideClearIcon ? (
        <InputButton {...CLEAR_ICON_STYLE} onPressIn={handleClear}>
          <Icon name="close" width="space-400" height="space-400" />
        </InputButton>
      ) : null}
      {showDropdownIcon ? (
        <Icon
          {...DROP_DOWN_ICON_STYLE}
          color="iconSecondary"
          name="arrowDropDown"
          width="space-600"
          height="space-600"
        />
      ) : null}
    </Box>
  );
}

export default forwardRef<TextInput, InputProps>(Input);
